<template>
  <div
    id="app"
    :class="{ 'full-screen': isFullScreen }">
    <vue-extend-layouts/>
    <info-modal-sb/>
    <NumberInfoModal />
    <password-confirm-modal-sb/>
    <ConfirmModalSb />
    <MicrophonePermission />
    <OrganisationSplash
      v-if="showSplash" />
  </div>
</template>

<script>
import { isDesktopApp } from '@todesktop/client-core/platform/todesktop';
import platform from 'platform-detect';
import { nativeWindow, app, notification } from '@todesktop/client-core';
import * as Sentry from '@sentry/browser';
import { mapGetters, mapActions } from 'vuex';
import VueExtendLayouts from 'vue-extend-layout';
import MicrophonePermission from '@/components/business/chat/partials/MicrophonePermission';
import OrganisationSplash from '@/components/organisation/OrganisationSplash';
import { saveLastOrgId } from '@/helpers/localStorage.helpers';
import ToasterMixin from '@/mixins/Toaster';
import NotificationSoundMixin from '@/mixins/NotificationSound';
import JuneMixin from '@/mixins/June';

export default {
  name: 'App',
  components: {
    VueExtendLayouts,
    MicrophonePermission,
    OrganisationSplash,
  },
  mixins: [
    ToasterMixin,
    NotificationSoundMixin,
    JuneMixin,
  ],
  data() {
    return {
      pusherIssueTimeout: null,
      pusherReconnectInterval: null,
      isFullScreen: false,
    };
  },
  computed: {
    ...mapGetters({
      userSignedOut: 'user/userSignedOut',
      userLoaded: 'user/userLoaded',
      showSplash: 'organisation/showSplash',
      echoConnectionLost: 'pusher/echoConnectionLost',
      lastUserId: 'user/lastUserId',
      currentOrganisation: 'organisation/currentOrganisation',
      pusherTooOld: 'pusher/pusherTooOld',
      // lastMessageTime: 'pusher/lastMessageTime',
      newRoute: 'pusher/newRoute',
      totalUnreadConversations: 'organisation/totalUnreadConversations',
      unreadCounter: 'chat/unreadCounter',
    }),
    countUnread() {
      let counter = 0;
      try {
        Object.keys(this.unreadCounter).forEach((key) => {
          counter += this.unreadCounter[key].counter;
        });
      } catch (error) { /* do nothing */ }

      return counter;
    },
  },
  watch: {
    $route(to, from) {
      if (to.meta.title) {
        const countText = this.countUnread ? `(${this.$options.filters.nineNinePlus(this.countUnread)}) ` : '';
        document.title = `${countText}${to.meta.title} | AI Number`;
      }

      if (this.countUnread) {
        this.changeFavIcon('/favicon-dot.ico');
      } else {
        this.changeFavIcon('/favicon.ico');
      }

      if (
        (from.name === 'AssistantTest' && to.name !== 'AIAssistantTest') ||
        (from.name !== 'AssistantTest' && to.name === 'AssistantTest')
      ) {
        // counters need to be reset via local storage because the component is remounted on new data and looses its state
        localStorage.setItem('aifiles:nCC', 0);
        localStorage.setItem('aifiles:cO', null);
      }
    },
    countUnread: {
      handler() {
        const countText = this.countUnread ? `(${this.$options.filters.nineNinePlus(this.countUnread)}) ` : '';
        const currentTitleWithoutCount = document.title.replace(/\(\d+\+?\)\s/, '');
        document.title = `${countText}${currentTitleWithoutCount}`;

        // set favicon
        if (this.countUnread) {
          this.changeFavIcon('/favicon-dot.ico');
        } else {
          this.changeFavIcon('/favicon.ico');
        }

        // set badge
        if (isDesktopApp()) {
          app.setBadgeCount(this.$options.filters.nineNinePlus(this.countUnread));
        }
      },
      immediate: true,
      deep: true,
    },
    userLoaded() {
      const userDevice = {
        app: 'web',
      };
      if (platform.windows) {
        userDevice.os = 'Windows';
      } else if (platform.linux) {
        userDevice.os = 'Linux';
      } else if (platform.macos) {
        userDevice.os = 'Mac';
      } else if (platform.android) {
        userDevice.os = 'Android';
      } else if (platform.ios) {
        userDevice.os = 'iOS';
      }
      if (window.todesktop) {
        userDevice.app = 'desktop';
        if (this.userLoaded) {
          this.joinDesktopPrivateChannel();
        } else {
          this.leaveDesktopPrivateChannel();
        }
      }

      if (this.userLoaded) {
        this.juneTrack('User device', userDevice);
      }
    },
    // TODO set two types of local storage items... those that celar on log out and those that don't.
    // Also prefix it with user id so more than one user can have localstorage data.
    userSignedOut(signedOut) {
      if (signedOut) {
        // clear timeout on the pusher import
        // this.clearInactivityTimeut();
        // preserve import reference in the local storage
        // eslint-disable-next-line
        const importUUID = Object.keys(localStorage)                                                 // eslint-disable-next-line
                                  .filter(key => key.startsWith('importUUID.'))                       // eslint-disable-next-line
                                  .map(key => ({ key, value: localStorage.getItem(key) }));
        // Bulk send UUID preserve
        // this.clearBulkSendInactivityTimeut();
        // preserve bulk send reference in the local storage
        // TODO - this will have to be handled differenty now when we have multiple businesses available. Probably by saving organization and businessId and not from the route
        const bulkSendUUID = localStorage.getItem(`_c_gm_pf_ns.${this.$route.params.merchant_id}`);
        const hideAvailabilityInfo = localStorage.getItem('hideAvailabilityInfo');
        const lastBusinessInFocus = localStorage.getItem('lastBusinessInFocus');
        const lastConversationInFocus = localStorage.getItem('lastConversationInFocus');
        const lastOrganisationInFocus = localStorage.getItem('lastOrganisationInFocus');
        const readSystemMessages = localStorage.getItem('readSystemMessages');
        const hideAnnouncement = localStorage.getItem('feature:AIassistant:6');
        localStorage.clear();
        // restoring values
        importUUID.forEach(importUUID => localStorage.setItem(importUUID.key, importUUID.value));
        if (bulkSendUUID) {
          localStorage.setItem(`_c_gm_pf_ns.${this.$route.params.merchant_id}`, bulkSendUUID);
        }
        localStorage.setItem('hideAvailabilityInfo', hideAvailabilityInfo);
        localStorage.setItem('lastOrganisationInFocus', lastOrganisationInFocus); // TODO refactor cleanup of local storage to not remove all but only some values so we do not have to repopulate it like this after clear.
        saveLastOrgId();
        localStorage.setItem('lastBusinessInFocus', lastBusinessInFocus);
        localStorage.setItem('lastConversationInFocus', lastConversationInFocus);
        localStorage.setItem('readSystemMessages', readSystemMessages);
        localStorage.setItem('feature:AIassistant:6', hideAnnouncement);
        this.$router.push({ name: 'LoginPage' });
        // this.turnOffEchoConnection();
        this.$nextTick(() => {
          this.$store.commit('reset');
        });

        if (window.todesktop) {
          app.setBadgeCount(0);
        }
      }
    },
    pusherTooOld() {
      // DO NOTHING for now. If we see this is an issue then we can uncomment the code below or simply refresh the page dinamically.
      // if (this.pusherTooOld) {
      //   this.$confirmPluginModal.show({
      //     title: 'Please restart',
      //     message: 'Your browser was offline for too long. Please reload to sync.',
      //     confirmButtonText: 'Reload',
      //     confirmCallback: () => { window.location.reload(); },
      //   });
      // }
    },
    // lastMessageTime() {
    //   // if (this.lastMessageTime && (Date.now() - this.lastMessageTime) > (3 * 3 * 1000)) {
    //     // this.pusherTooOld = true;
    //     this.pusherIssueTimeout = setTimeout(() => {
    //       console.log('reload');
    //     }, 3000);
    //   // }
    // },
    echoConnectionLost(connectionLost) {
      if (connectionLost) {
        // this.$refs.connectionLostModal.show();
        // pusher reconnect - IMPORTANT - do not reconnect pusher because old event listeners are still present
        // this.joinOrganisationChannel(this.currentOrganisation.id);
      } else {
        // this.$refs.connectionLostModal.hide();
      }
    },
    newRoute: {
      handler() {
        this.$router.push({
          name: this.newRoute.name,
          params: this.newRoute.params,
        }).catch(() => {});
      },
      deep: true,
    },
  },
  mounted() {
    window.sbToast = this.$bvToast;
    window.createElement = this.$createElement;
    window.vueFilters = this.$options.filters;
    window.eventBus.$on('show-top-toast', (data) => {
      this.showToaster(data.message, data.icon, data.variant, data.emojiIcon);
    });

    window.addEventListener('blur', this.onBlur);
    window.addEventListener('focus', this.onFocus);

    if (window.todesktop) {
      nativeWindow.on('resize', this.checkIfMaximized);
    }

    // Show initial welcome notification on first desktop app load
    const welcomeNotificationShown = localStorage.getItem('welcome_notification');
    if (window.todesktop && !welcomeNotificationShown) {
      notification.create({
        title: 'Welcome to AI Number for Desktop 🎉',
        body: 'If you have any questions feel free to contact our support at support@ainumber.com',
        silent: true,
      }).then((ref) => {
        this.notifySound();
        notification.show({ ref });
      });
      localStorage.setItem('welcome_notification', true);
    }
  },
  beforeDestroy() {
    nativeWindow.removeAllListeners();

    window.removeEventListener('blur', this.onBlur);
    window.removeEventListener('focus', this.onFocus);

    if (window.todesktop) {
      this.leaveDesktopPrivateChannel();
    }
  },
  methods: {
    ...mapActions({
      // turnOffEchoConnection: 'pusher/turnOffEchoConnection',
      // clearInactivityTimeut: 'pusher/clearInactivityTimeut',
      // clearBulkSendInactivityTimeut: 'pusher/clearBulkSendInactivityTimeut',
      joinOrganisationChannel: 'pusher/joinOrganisationChannel',
      joinDesktopPrivateChannel: 'pusher/joinDesktopPrivateChannel',
      leaveDesktopPrivateChannel: 'pusher/leaveDesktopPrivateChannel',
    }),
    checkIfMaximized() {
      nativeWindow.isFullscreen().then((state) => {
        this.isFullScreen = state;
      });
    },
    changeFavIcon(src) {
      const favicon = document.getElementById('favicon');
      favicon.setAttribute('href', src);
    },
  },
};
</script>
